/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	Input,
	OnDestroy,
	OnInit,
	Output,
	EventEmitter,
	DestroyRef,
	inject,
	ViewChild,
	ElementRef,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { Priority } from '@touchplan/common';
import { ProjectMember } from '@touchplan/common';
import { Role } from '@touchplan/common';
import { Location } from '@touchplan/common';
import { TicketContext } from 'src/app/modules/ticket-edit-panel/ticket-context';
import { TicketType } from '@touchplan/common';
import * as appStrings from '@touchplan/strings';
import { CustomFieldsApi } from 'src/app/app-data/state/CustomFieldsApi';
import { GroupedTicket, CalendarWorkdays, TicketFieldsService } from '@touchplan/ticket';
import { map, pluck, take, withLatestFrom, filter } from 'rxjs/operators';
import { TicketFormManager } from '../../services/ticket-form-manager/ticket-form-manager.service';
import { MatDialog } from '@angular/material/dialog';
import { TicketEditUtils } from '../../services/ticket-edit-utils.service';
import {
	ITicketEditUpdateStatusDialogData,
	IUpdateFields,
	TicketEditDialogComponent,
} from '../ticket-edit-dialog/ticket-edit-dialog.component';
import { ActualPhase, TicketFields } from '@touchplan/common';
import { Plan } from '@touchplan/common';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { Variance } from '@touchplan/common';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { PlanService } from 'mobile/app/services/plan/plan.service';
import { DefaultLoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { TicketEditCommonService } from '../../services/ticket-edit-common.service';
import { TicketFormControls } from '../../services/ticket-form-manager/ticket-form-controls.enum';
import * as strings from '@touchplan/strings';
import { GranularEnabledService } from '../../../granular-time/granular-enabled.service';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
	selector: 'docked-ticket',
	templateUrl: './docked-ticket-container.component.html',
	styleUrls: ['./docked-ticket-container.component.scss'],
	providers: [
		TicketFormManager,
		TicketEditUtils,
		TicketFieldsService,
		LoggerServiceFactory.provide('DockedTicketContainer'),
	],
})
export class DockedTicketContainerComponent implements OnInit, OnDestroy {
	private destroyRef = inject(DestroyRef);

	@Input() public ticket$: Observable<GroupedTicket>;
	@Input() public plan$: Observable<Plan>;
	@Input() public ticketId: string;
	@Input() public roles$: Observable<Role[]>;
	@Input() public locations$: Observable<Location[]>;
	@Input() public projectMembers$: Observable<ProjectMember[]>;
	@Input() public priorityList$: Observable<Priority[]>;
	@Input() public varianceList$: Observable<Variance[]>;
	@Input() public ticketContext: TicketContext;
	@Input() public customFieldsApi: CustomFieldsApi;
	@Input() public calendarWorkdays: CalendarWorkdays;
	public saveCustomFieldsRequested$ = new Subject<void>();
	public cancelCustomFieldsRequested$ = new Subject<void>();
	@Input() public planAccessCheck: PlanAccessCheck;
	@Input() public allowList$: Observable<string[]>;
	//disable by default for mobile reasons (maybe swap later). Current iteration does not support changing after load
	@Input() public disableForm = true;
	/** emit on form changes with data to be written to the ticket model(s) */
	@Output() public ticketDataChanged = new EventEmitter<{
		ticket: GroupedTicket;
		updateData: TicketFields;
	}>();
	/** emit when the current ticket edit data should be saved */
	@Output() public persistTicketData = new EventEmitter<GroupedTicket>();
	/** emit when the docked ticket module wants to be closed */
	@Output() public shouldClose = new EventEmitter<GroupedTicket>();
	/** emit to reset the ticket edit view to the current database data */
	@Output() public revertToDatabase = new EventEmitter();
	@ViewChild('customFieldsForm') customFieldsFormElement!: ElementRef<HTMLFormElement>;

	public isFileAttachEnabled$: Observable<boolean>;
	public ticketForm: UntypedFormGroup;
	public editDialogFormGroupChange$: Subject<IUpdateFields> = new Subject();
	public TicketType = TicketType;
	public customFieldsListFormGroup;
	public appStrings = appStrings;
	public activeLineDate$: Observable<string>;
	public isCloseInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	// Save button disabled
	public buttonSaveDisabled = false;

	public hasAccess$: Observable<boolean>;
	public showStartWorkButton$: Observable<boolean>;

	private commonServiceIdentifier: string;

	private readonly fiveSeconds = 5 * 1000;

	private ticketFormSubmitted = false;
	protected ticketFormHasErrors = false;
	protected ticketCustomFieldsFormHasErrors = false;

	protected activeTab: 'details' | 'customFields' | 'attachments' = 'details';

	constructor(
		public dialog: MatDialog,
		private ticketFormManager: TicketFormManager,
		private ticketEditUtils: TicketEditUtils,
		private planService: PlanService,
		private featureFlagManager: FeatureFlagManager,
		private ticketFieldsService: TicketFieldsService,
		private ticketEditCommonService: TicketEditCommonService,
		private logger: DefaultLoggerService,
		private granularEnabledService: GranularEnabledService,
		private bannerMessageHandler: BannerMessageHandler // BEGIN PLACEHOLDER REPLACE AS PART OF TP-7376 // - some temp test code // private onDemandReschedule: OnDemandReschedule // END PLACEHOLDER REPLACE AS PART OF TP-7376
	) {}

	public ngOnInit(): void {
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7376
		// - temp test code
		// this.ticket$.pipe(
		// 	filter(ticket => !!ticket && ticket.srcList.size > 0),
		// 	take(1)
		// ).subscribe(ticket => {
		// 	console.log('ticket ready', ticket);
		// 	const data = {
		// 		plannedStart: ticket.view.liveStart!,
		// 		plannedFinish: ticket.view.liveFinish!
		// 	}
		// 	console.time('schedule')
		// 	this.onDemandReschedule.requestWithStartAndFinish(this.ticketContext.projectId, this.ticketContext.planId, ticket.$id, data).subscribe(s => {
		// 		console.log('response get', s);
		// 		console.timeEnd('schedule')
		// 		this.ticketEditUtils.updateLiveDurationDays(s.derivedDurationDays);
		// 	});
		// });
		// END PLACEHOLDER REPLACE AS PART OF TP-7376

		// Watch plan and keep ticket fields service updated accordingly
		this.plan$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((plan) => {
			this.ticketFieldsService.setup(
				this.calendarWorkdays,
				plan.view.activeLineDate,
				plan.view.activeLineX,
				plan.view.calDayWidthPixels
			);
		});

		this.activeLineDate$ = this.plan$.pipe(map((plan) => plan.view?.activeLineDate));

		// NOTE: the rest of this will be replaced by https://mocasystems.atlassian.net/browse/TP-3212
		// if the gtp flag changes also changes, patch the last ticket values again using the new gtp flag value
		// ignore input while closing the form - this prevents calling patchForm with a potentially empty ticket
		combineLatest([this.ticket$, this.granularEnabledService.isGranularEnabled$])
			.pipe(
				withLatestFrom(this.isCloseInProgress$),
				filter(([[_ticket, _isGtpEnabled], isClosing]) => !isClosing),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(([[ticket]]) => {
				if (this.ticketForm) {
					this.ticketFormManager.patchForm(ticket);
				} else {
					this.ticketForm = this.ticketFormManager.create(
						(ticket?.view.type as TicketType) || TicketType.Task,
						ticket,
						this.disableForm
					);
					this.customFieldsListFormGroup = this.ticketForm.get('customFields') as UntypedFormGroup;
				}
			});

		this.ticketEditUtils.init(this.ticket$);
		this.hasAccess$ = this.planAccessCheck.hasAccessToTicket$(this.ticket$);

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6142
		// TODO: use actualPhase attribute instead of manual planed phase check using actuals
		this.showStartWorkButton$ = this.ticket$.pipe(
			takeUntilDestroyed(this.destroyRef),
			map(
				(ticket) =>
					(!ticket.view.actualStart && !ticket.view.actualFinish && ticket.view.type === TicketType.Task) ||
					(!ticket.view.actualStart && !ticket.view.actualFinish && !ticket.view.type)
			)
		);
		// END PLACEHOLDER REPLACE AS PART OF TP-6142

		this.registerOutputs();
		this.isFileAttachEnabled$ = this.fileAttachEnabled$();

		// Form group changes subscription
		// currently this is serving as a filter against updating "invalid values"
		this.editDialogFormGroupChange$.subscribe((changes) => {
			this.logger.log('Updated values from Update status dialog: ', changes);
			Object.keys(changes).forEach((key) => {
				switch (key) {
					case 'liveDurationDays':
						this.ticketEditUtils.updateLiveDurationDays(changes[key] as number);
						break;
					case 'liveCrewSize':
						this.ticketEditUtils.updateLiveCrewSize(changes[key] as number);
						break;
					case 'liveFinish':
						this.ticketEditUtils.updateLiveFinish(changes[key] as string);
						break;
					case 'liveStart':
						this.ticketEditUtils.updateLiveStart(changes[key] as string);
						break;
					case 'liveWorkingDays':
						this.ticketEditUtils.updateWorkdays(changes[key] as boolean[]);
						break;
					case 'varianceReason':
						this.ticketEditUtils.updateVarianceReason(changes[key] as string);
						break;
					case 'varianceComment':
						this.ticketEditUtils.updateVarianceComment(changes[key] as string);
						break;
					default:
						//currently this will be catch the adapters, and thus ignore their emissions. Which is expected, because of the
						//adapters doing their adapting
						//should _probably_ catch that further upstream
						this.logger.log('tried to update invalid control: ', key);
						break;
				}
			});
		});

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-4914
		// Just generally think more if this can be done better. The valueChanges subscription _might_ be fine
		//TODO: move elsewhere/ solve better/ type
		function popObject<T>(obj: any, key: string): T {
			const thing = obj[key];
			delete obj[key];
			return thing;
		}

		// Outputs from the normal form, should probably entirely disable this subscription if the form is disabled (mobile)...
		this.ticketFormManager.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((changes) => {
			this.logger.log('Updated values from normal form: ', changes);

			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7102
			//TODO decide "when", first, last, within updateFields?
			// - functionally this is an "output transform"
			this.ticketEditUtils.correctJoinedData(changes);
			// END PLACEHOLDER REPLACE AS PART OF TP-7102

			//changes is an object with a directory of fields that have changed. On a technical level multiple fields could be updated
			// at once, on a practical level, it will probably be one at a time.
			// So... split out special fields, call their special methods, if anything's left, call the standard method
			if (changes['liveDurationDays']) {
				this.ticketEditUtils.updateLiveDurationDays(popObject<number>(changes, 'liveDurationDays'));
			}
			if (changes['livePlanGranularity']) {
				this.ticketEditUtils.updateLivePlanGranularity(popObject<string>(changes, 'livePlanGranularity'));
			}
			if (changes['liveFinish']) {
				this.ticketEditUtils.updateLiveFinish(popObject<string>(changes, 'liveFinish'));
			}
			if (changes['liveStart']) {
				this.ticketEditUtils.updateLiveStart(popObject<string>(changes, 'liveStart'));
			}
			if (changes['liveWorkingDays']) {
				this.ticketEditUtils.updateWorkdays(popObject<boolean[]>(changes, 'liveWorkingDays'));
			}
			if (changes['assignedProjectMember']) {
				this.ticketEditUtils.updateConstraintAssignment(
					popObject<ProjectMember>(changes, 'assignedProjectMember')
				);
			}

			//all the special behavior is done, now do the "normal" stuff
			if (Object.keys(changes).length > 0) {
				this.ticketEditUtils.updateFields(changes);
			}
		});
		// END PLACEHOLDER REPLACE AS PART OF TP-4914

		//init the external data _after_ everything else
		this.commonServiceIdentifier = this.ticketEditCommonService.registerInstance({
			isValid$: this.ticketFormManager.isValid$(),
			isValidSync: () => {
				return this.ticketFormManager.isValidSync();
			},
			isDirty$: this.ticketFormManager.isDirty(),
			isDirtySync: () => {
				return this.ticketFormManager.isDirtySync();
			},
			ticket$: this.ticket$,
			closeEdit$: this.ticketEditUtils.wantsToClose$,
			trySave: () => this.trySave(),
			applyWorkdaysToForm: (workdays: boolean[]) => {
				this.ticketFormManager.userFormControlUpdate(TicketFormControls.liveWorkingDays, workdays);
			},
		});

		this.ticketForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.updateTicketFormBadge();
		});

		this.customFieldsListFormGroup.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.updateCustomFieldsFormBadge();
		});
	}

	private registerOutputs() {
		//changed data - ignore if close is in progress
		this.ticketEditUtils.changedData$
			.pipe(
				withLatestFrom(this.ticketEditUtils.groupedTicket$, this.isCloseInProgress$),
				filter(([_updateData, _ticket, isClosing]) => !isClosing),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(([updateData, ticket]) => {
				this.ticketDataChanged.emit({ ticket, updateData });
			});

		//persist
		this.ticketEditUtils.persistChanges$
			.pipe(
				withLatestFrom(this.ticketEditUtils.groupedTicket$),
				pluck(1),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((ticket) => {
				this.persistTicketData.emit(ticket);
			});

		//closing edit/ nav-ing back to the ticket list
		this.ticketEditUtils.wantsToClose$
			.pipe(
				withLatestFrom(this.ticketEditUtils.groupedTicket$),
				pluck(1),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((ticket) => {
				this.shouldClose.emit(ticket);
			});

		//revert to last saved data
		this.ticketEditUtils.wantsToRevertData$
			.pipe(
				withLatestFrom(this.ticketEditUtils.groupedTicket$),
				pluck(1),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((ticket) => {
				this.revertToDatabase.emit(ticket);
			});
	}

	public ngOnDestroy() {
		this.ticketEditCommonService.unregisterInstance(this.commonServiceIdentifier);

		this.editDialogFormGroupChange$.complete();
		this.isCloseInProgress$.complete();
	}

	//TODO: Implement logic for `Show Planned Values` buttons
	public updateStatus() {
		//if it's currently planned, the update status button should actualize it
		if (
			this.ticketEditUtils.groupedTicketSync.view.actualPhase === ActualPhase.planned ||
			this.ticketEditUtils.groupedTicketSync.view.actualPhase === ActualPhase.started
		) {
			this.ticketEditUtils.convertPlannedToActual();
		}

		const ticketEditDialogData = {
			title: appStrings.UPDATE_STATUS,
			saveButtonText: appStrings.SAVE,
			cancelButtonText: appStrings.CANCEL,
			ticket$: this.ticket$,
			roleList$: this.roles$,
			locationList$: this.locations$,
			projectMembers$: this.projectMembers$,
			priorityList$: this.priorityList$,
			varianceList$: this.varianceList$,
			editDialogFormGroupChange$: this.editDialogFormGroupChange$,
			isDisabled: false,
			calendarWorkdays: this.calendarWorkdays,
			activeLineDate$: this.activeLineDate$,
		} as ITicketEditUpdateStatusDialogData;

		const dialogRef = this.dialog.open(TicketEditDialogComponent, {
			data: ticketEditDialogData,
			maxWidth: '95vw',
			disableClose: false,
		});

		this.ticket$.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe((ticket) => {
			analyticsEventsService.updateStatusButtonClicked(this.ticketContext.planId, ticket.$id);
		});

		//true - user saved
		//false - user canceled
		dialogRef.afterClosed().subscribe((shouldSave: boolean) => {
			if (shouldSave) {
				//save (persist as soon as the edit dialog closes)
				// TODO: Avoid persisting immediately without user interaction (clicking save) for the desktop version..
				this.ticketEditUtils.saveChanges();
			} else {
				this.logger.log('update status canceled');
				this.ticketEditUtils.revertData();
			}
		});
	}

	public onStartWork() {
		if (this.ticketEditUtils.groupedTicketSync.view.actualPhase === ActualPhase.planned) {
			// TODO: Avoid persisting immediately without user interaction (clicking save) for the desktop version..
			this.ticketEditUtils.convertPlannedToStart();
			this.ticketEditUtils.saveChanges();
			this.ticket$.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe((ticket) => {
				analyticsEventsService.startWork(this.ticketContext.planId, ticket.$id);
			});
		}
	}

	public showPlannedValues() {
		// TODO: later stuff
	}

	/**
	 * Save the form. Logs notice if dirty.
	 */
	public trySave() {
		if (this.ticketForm.dirty) {
			Logging.notice(strings.TICKET_SAVED_IMPLICITLY);
		}
		this.saveOrClose();
	}

	/**
	 * Save or close the ticket form.
	 * If the form is invalid, a banner message is displayed and the form is not saved or close.
	 * If it's valid, it will be saved and the form will be closed.
	 */
	public saveOrClose() {
		this.ticketFormSubmitted = true;

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7410
		if (this.activeTab === 'customFields') {
			// If the active tab is 'customFields' and the form is visible, manually trigger validation by submitting the custom fields form.
			this.customFieldsFormElement.nativeElement.requestSubmit();
		}

		if (this.ticketForm.invalid) {
			// When custom fields are invalid, emit saveCustomFieldsRequested$ to prompt the custom fields component to handle validation.
			// This is only done if custom fields are invalid; otherwise, the form will be saved normally.
			if (this.customFieldsListFormGroup.invalid) {
				this.saveCustomFieldsRequested$.next();
			}
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7410
			this.updateTicketFormBadge();
			this.updateCustomFieldsFormBadge();
			this.bannerMessageHandler.displayWarningMessage(appStrings.MISSING_FIELDS_WARNING_MESSAGE);
			return;
		}

		// Put up spinner overlay with a 5 second timeout
		this.isCloseInProgress$.next(true);
		setTimeout(() => {
			this.isCloseInProgress$.next(false);
		}, this.fiveSeconds);

		this.saveCustomFieldsRequested$.next();
		this.ticketEditUtils.saveChanges();
		this.ticketEditUtils.closeForm();

		// TODO: ticket context should probably know if we're floating or not
		const isFloating = false;
		this.ticket$.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe((ticket) => {
			analyticsEventsService.editedATicket(
				ticket.$id,
				this.ticketContext.planId,
				this.ticketContext.planIsLockedSync,
				ticket.view.liveLastPromise && ticket.view.liveLastPromise.data.plannedFinish
					? ticket.view.liveLastPromise.data.plannedFinish
					: null,
				isFloating
			);
		});
	}

	// TODO: this method is never called, nor am I aware of "when" it should be called in v1
	public cancel(event) {
		//TODO: Implement logic for `cancel` button
		this.logger.log('Cancel', event);
		this.ticketEditUtils.revertData();
		this.cancelCustomFieldsRequested$.next();
		this.ticketEditUtils.closeForm();
	}

	// public updateTicketForm(event) {
	// 	//TODO: Implement logic for handling form changes.
	// 	this.logger.log('Emitted changes: ', event);
	// }

	public getLeftButtonText(isActualized: boolean) {
		return isActualized ? appStrings.SHOW_PLANNED_VALUES : appStrings.UPDATE_STATUS;
	}

	/**
	 * File attach is enabled only when two conditions are satisfied:
	 * (1) The feature flag, "fileAttachEnabled", is true, and
	 * (2) Project level configuration is not false.  That is, if a project
	 *     has a boolean "fileAttachEnabled" flag set to false, file attach
	 *     for that project is disabled.  But if that flag is true, OR if
	 *     that flag isn't set for a project, file attach is enabled for
	 *     that project
	 */
	private fileAttachEnabled$(): Observable<boolean> {
		return combineLatest([
			this.planService.project$.pipe(
				map((project) => project?.fileAttachEnabled ?? true) // true is actually the default
			),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.FILE_ATTACH_ENABLED),
		]).pipe(map((value) => value[0] && value[1]));
	}

	private updateTicketFormBadge() {
		const { customFields, ...restOfControls } = this.ticketForm.controls;
		this.ticketFormHasErrors = Object.values(restOfControls).some(
			(c) => c.invalid && (c.dirty || c.touched || this.ticketFormSubmitted)
		);
	}

	private updateCustomFieldsFormBadge() {
		this.ticketCustomFieldsFormHasErrors =
			this.customFieldsListFormGroup.invalid &&
			(this.customFieldsListFormGroup.dirty ||
				this.customFieldsListFormGroup.touched ||
				this.ticketFormSubmitted);
	}

	protected onTabChange(event: MatTabChangeEvent) {
		switch (event.index) {
			case 0:
				this.activeTab = 'details';
				break;
			case 1:
				this.activeTab = 'customFields';
				break;
			case 2:
				this.activeTab = 'attachments';
				break;
			default:
				this.activeTab = 'details';
				break;
		}
	}
}
